import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import PolicyEditor from "@/components/policy/PolicyEditor";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

const PolicyCreator = () => {
  const [searchParams] = useSearchParams();
  const editPolicyId = searchParams.get('edit');
  const [checkingAuth, setCheckingAuth] = useState(false);
  const [canEdit, setCanEdit] = useState(true);
  const { profile, hasAnyRole } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    const checkEditPermission = async () => {
      if (!editPolicyId || !profile?.id) {
        setCheckingAuth(false);
        return;
      }

      setCheckingAuth(true);
      try {
        const { data: policy, error } = await supabase
          .from('policies')
          .select('created_by')
          .eq('id', editPolicyId)
          .single();

        if (error) throw error;

        if (policy) {
          const isCreator = policy.created_by === profile.id;
          const isAdmin = hasAnyRole(['admin']);
          setCanEdit(isCreator || isAdmin);
          
          if (!isCreator && !isAdmin) {
            toast({
              title: "Permission Denied",
              description: "Only the creator or an admin can edit this policy.",
              variant: "destructive",
            });
          }
        }
      } catch (error) {
        console.error('Error checking edit permission:', error);
        setCanEdit(false);
        toast({
          title: "Error",
          description: "Failed to verify permissions.",
          variant: "destructive",
        });
      } finally {
        setCheckingAuth(false);
      }
    };

    checkEditPermission();
  }, [editPolicyId, profile?.id, hasAnyRole, toast]);

  if (checkingAuth) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Checking permissions...</p>
        </div>
      </div>
    );
  }

  if (!canEdit && editPolicyId) {
    return (
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="text-destructive">Permission Denied</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              You do not have permission to edit this policy. Only the creator or an admin can edit this policy.
            </p>
            <Button 
              variant="outline" 
              onClick={() => window.history.back()}
            >
              Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <PolicyEditor 
      policyId={editPolicyId || undefined}
      onSave={(policyId) => {
        // Navigate to the policy view after save
        window.location.href = `/policy/${policyId}`;
      }}
    />
  );
};

export default PolicyCreator;